import React from 'react';
import {ResearchLink, Tag} from '../types';

interface ResearchPillsProps {
  tag: Tag;
  maxItems?: number;
}

export const extractLinksFromResearch = (raw: string): ResearchLink[] => {
  if (!raw) return [];

  const results: ResearchLink[] = [];

  // Capture all <research>...</research> blocks; if none, treat entire string as a single block
  const researchBlocks: string[] = [];
  const blockRegex = /<research>([\s\S]*?)<\/research>/g;
  let blockMatch: RegExpExecArray | null;
  while ((blockMatch = blockRegex.exec(raw)) !== null) {
    researchBlocks.push(blockMatch[1].trim());
  }
  if (researchBlocks.length === 0) {
    researchBlocks.push(raw.trim());
  }

  const pushItem = (item: Partial<ResearchLink>) => {
    if (!item || !item.url) return;
    try {
      const urlObj = new URL(item.url);
      const isHttpProtocol = urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
      const looksLikeEmail = /@/.test(item.url) || urlObj.protocol === 'mailto:';
      if (!isHttpProtocol || looksLikeEmail) return;
      const host = item.host || (item as any).domain || urlObj.hostname;
      results.push({
        title: item.title || host || urlObj.hostname,
        url: item.url,
        host: typeof host === 'string' ? host : urlObj.hostname,
        favicon: item.favicon,
      });
    } catch {
      // skip invalid URL
    }
  };

  for (const content of researchBlocks) {
    // Try to parse as JSON (handles both array root and { content: [...] })
    const attempts: string[] = [content, content.replace(/'/g, '"')];
    let parsed: any | null = null;
    for (const attempt of attempts) {
      try {
        parsed = JSON.parse(attempt);
        break;
      } catch {
        // continue
      }
    }

    if (parsed && (Array.isArray(parsed) || parsed?.content)) {
      const items = Array.isArray(parsed) ? parsed : parsed.content;
      if (Array.isArray(items)) {
        for (const obj of items) {
          if (obj && typeof obj === 'object' && obj.url) {
            pushItem({
              title: obj.title,
              url: obj.url,
              host: obj.domain,
              favicon: obj.favicon,
            });
          }
        }
      }
      continue;
    }

    // Fallback: tolerant extraction of object-like segments inside the block
    const objectRegex = /\{[\s\S]*?\}/g;
    let objMatch: RegExpExecArray | null;
    while ((objMatch = objectRegex.exec(content)) !== null) {
      const objText = objMatch[0];
      const getValue = (key: string): string | undefined => {
        const rx = new RegExp(`["']${key}["']\\s*:\\s*(["'])([\\s\\S]*?)\\1`);
        const m = objText.match(rx);
        return m ? m[2] : undefined;
      };
      const url = getValue('url');
      if (!url) continue;
      const title = getValue('title');
      const domain = getValue('domain');
      const favicon = getValue('favicon');
      if (url && /^https?:\/\//i.test(url)) {
        pushItem({ title, url, host: domain, favicon });
      }
    }
  }

  return dedupeByUrl(results);
};

const dedupeByUrl = (arr: { title: string; url: string; host?: string; favicon?: string }[]) => {
  const seen = new Set<string>();
  const out: { title: string; url: string; host?: string; favicon?: string }[] = [];
  for (const item of arr) {
    if (!seen.has(item.url)) {
      seen.add(item.url);
      out.push(item);
    }
  }
  return out;
};

const ResearchPills: React.FC<ResearchPillsProps> = ({ tag, maxItems = 3 }) => {
  const links = extractLinksFromResearch(tag?.content || '');
  const hasLinks = links && links.length > 0;
  const [expanded, setExpanded] = React.useState(false);
  const [animateReveal, setAnimateReveal] = React.useState(false);

  React.useEffect(() => {
    if (expanded) {
      const id = setTimeout(() => setAnimateReveal(true), 30);
      return () => clearTimeout(id);
    } else {
      setAnimateReveal(false);
    }
  }, [expanded]);

  if (!hasLinks) return null;

  const visible = expanded ? links : links.slice(0, maxItems);
  const remaining = links.length - (expanded ? links.length : visible.length);

  return (
    <div className="research-pills ">
      {visible.map((l, idx) => {
        const isRevealed = expanded && idx >= maxItems;
        const delay = isRevealed ? `${(idx - maxItems) * 50}ms` : '0ms';
        const style = isRevealed
          ? {
              opacity: animateReveal ? 1 : 0,
              transform: animateReveal ? 'none' : 'translateY(4px) scale(0.98)',
              transition: 'opacity 200ms ease-out, transform 200ms ease-out',
              transitionDelay: delay,
            }
          : undefined;
        let displayHost = l.host || '';
        try { displayHost = new URL(l.url).hostname; } catch {}
        const shortHost = (displayHost || '').replace(/^www\./, '');
        const faviconSrc = l.favicon || (displayHost ? `https://www.google.com/s2/favicons?domain=${displayHost}&sz=64` : '');
        return (
          <span className="research-pill-wrap" key={`${l.url}-${idx}`} style={style as any}>
            <a
              className="research-pill"
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {faviconSrc ? (
                <img src={faviconSrc} alt="" className="w-3 h-3 rounded-full inline-block" />
              ) : null}
              <span className="ml-1 opacity-70 truncate max-w-[100px]">{shortHost}</span>
            
            </a>
            <span className="pill-tooltip">
              <div className="pill-tooltip-card">
                <div className="pill-tooltip-header">
                  {faviconSrc ? <img src={faviconSrc} alt="" className="pill-tooltip-favicon" /> : null}
                  <span className="pill-tooltip-domain">{displayHost}</span>
                </div>
                <div className="pill-tooltip-title">{l.title}</div>
              </div>
            </span>
          </span>
        );
      })}
      {remaining > 0 && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="research-pill research-pill-muted text-gray-500 dark:text-gray-400"
          title="Show all sources"
        >
          +{remaining} more
        </button>
      )}
    </div>
  );
};

export default ResearchPills; 